import React, { useEffect, useState, useRef, Component } from 'react';
import { Sidebar } from '../components/Sidebar';
import { Header } from '../components/Header';
import { ClockIcon, EditIcon, SparklesIcon, PhoneIcon, MailIcon, MessageCircleIcon, PlayIcon, PauseIcon, Volume2Icon, BuildingIcon, CarIcon, RefreshCwIcon, XIcon, PhoneCallIcon, StopCircleIcon, MicIcon, UserIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { WhatsAppChatModal } from '../components/whatsapp/WhatsAppChatModal';
import { LeadDetailOverlay } from '../components/leads/LeadDetailOverlay';
import { WhatsAppLead } from '../types/whatsapp';
type LeadType = 'sales' | 'service' | 'non-sales' | 'feedback';
type LeadStatus = 'cold' | 'warm' | 'hot' | 'completed' | 'partial' | 'loading';
type CallStatus = 'pending' | 'completed' | 'callback-requested' | 'not-available';
interface Lead {
  id: number;
  name: string;
  initials: string;
  type: string;
  email: string;
  phone: string;
  dealer: string;
  dealerCode: string;
  carModel: string;
  status: LeadStatus;
  callStatus: CallStatus;
  hasAI: boolean;
  aiResponse?: string;
  hasRecording: boolean;
  recordingDuration?: string;
  tags?: string[];
  multiVisit?: boolean;
}
const SALES_LEADS: Lead[] = [{
  id: 1,
  name: 'Zainaka',
  initials: 'ZA',
  type: 'Customer Meeting',
  email: 'Zainaka@gmail.com',
  phone: '+91 7019722046',
  dealer: 'Koncept Auto',
  dealerCode: 'HR407',
  carModel: 'Thar Roxx',
  status: 'cold',
  callStatus: 'pending',
  hasAI: true,
  aiResponse: 'Answered By AI',
  hasRecording: true,
  recordingDuration: '0:32',
  tags: ['Completed'],
  multiVisit: true
}, {
  id: 2,
  name: 'Sunil Yadav',
  initials: 'SY',
  type: 'Invoice',
  email: 'N/A',
  phone: '+91 7011523526',
  dealer: 'Koncept Auto',
  dealerCode: 'HR407',
  carModel: 'Bolero Neo',
  status: 'warm',
  callStatus: 'pending',
  hasAI: true,
  aiResponse: 'Analysed By AI',
  hasRecording: false,
  tags: ['Partial']
}, {
  id: 3,
  name: 'Data Loading...',
  initials: 'XX',
  type: 'N/A',
  email: 'N/A',
  phone: '+91 9773696968',
  dealer: 'Koncept Auto',
  dealerCode: 'HR407',
  carModel: 'Scorpio-N',
  status: 'cold',
  callStatus: 'callback-requested',
  hasAI: true,
  hasRecording: true,
  recordingDuration: '0:32'
}, {
  id: 4,
  name: 'Priya Sharma',
  initials: 'PS',
  type: 'Test Drive',
  email: 'priya.sharma@gmail.com',
  phone: '+91 9876543210',
  dealer: 'Mumbai Motors',
  dealerCode: 'MH401',
  carModel: 'XUV700',
  status: 'hot',
  callStatus: 'completed',
  hasAI: false,
  hasRecording: true,
  recordingDuration: '1:45',
  tags: ['Completed']
}, {
  id: 5,
  name: 'Rahul Mehta',
  initials: 'RM',
  type: 'Finance Query',
  email: 'rahul.m@yahoo.com',
  phone: '+91 9123456789',
  dealer: 'Delhi Auto Hub',
  dealerCode: 'DL302',
  carModel: 'Thar',
  status: 'warm',
  callStatus: 'pending',
  hasAI: true,
  aiResponse: 'Answered By AI',
  hasRecording: false
}];
// Voice Agent Overlay Component
function VoiceAgentOverlay({
  isOpen,
  onClose,
  leadName
}: {
  isOpen: boolean;
  onClose: () => void;
  leadName: string;
}) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  useEffect(() => {
    if (isOpen && audioRef.current) {
      audioRef.current.play().then(() => {
        setIsPlaying(true);
      }).catch(err => {
        console.log('Auto-play prevented:', err);
      });
    }
    if (!isOpen && audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlaying(false);
      setCurrentTime(0);
    }
  }, [isOpen]);
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
    const handleLoadedMetadata = () => setDuration(audio.duration);
    const handleEnded = () => setIsPlaying(false);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('ended', handleEnded);
    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('ended', handleEnded);
    };
  }, []);
  const togglePlayPause = () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play();
      setIsPlaying(true);
    }
  };
  const handleStop = () => {
    if (!audioRef.current) return;
    audioRef.current.pause();
    audioRef.current.currentTime = 0;
    setIsPlaying(false);
    setCurrentTime(0);
  };
  const handleClose = () => {
    handleStop();
    onClose();
  };
  const formatTime = (time: number) => {
    const mins = Math.floor(time / 60);
    const secs = Math.floor(time % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  const progress = duration > 0 ? currentTime / duration * 100 : 0;
  if (!isOpen) return null;
  return <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999] flex items-center justify-center" onClick={handleClose} data-id="element-2183">
      <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-3xl shadow-2xl w-full max-w-md mx-4 overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-2184">
        {/* Header */}
        <div className="p-6 pb-4" data-id="element-2185">
          <div className="flex items-center justify-between mb-6" data-id="element-2186">
            <div className="flex items-center gap-3" data-id="element-2187">
              <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center" data-id="element-2188">
                <MicIcon size={20} className="text-blue-400" data-id="element-2189" />
              </div>
              <div data-id="element-2190">
                <h3 className="text-white font-semibold" data-id="element-2191">Voice Agent</h3>
                <p className="text-slate-400 text-xs" data-id="element-2192">AI-Powered Call</p>
              </div>
            </div>
            <button onClick={handleClose} className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/20 transition-colors" data-id="element-2193">
              <XIcon size={16} data-id="element-2194" />
            </button>
          </div>

          {/* Call Info */}
          <div className="text-center mb-8" data-id="element-2195">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 mx-auto mb-4 flex items-center justify-center shadow-lg shadow-blue-500/30" data-id="element-2196">
              <UserIcon size={36} className="text-white" data-id="element-2197" />
            </div>
            <h2 className="text-white text-xl font-semibold mb-1" data-id="element-2198">
              {leadName}
            </h2>
            <p className="text-slate-400 text-sm" data-id="element-2199">
              {isPlaying ? 'Call in progress...' : 'Call paused'}
            </p>
          </div>

          {/* Waveform Visualization */}
          <div className="flex items-center justify-center gap-1 h-12 mb-6" data-id="element-2200">
            {[...Array(20)].map((_, i) => <div key={i} className={`w-1 rounded-full transition-all duration-150 ${isPlaying ? 'bg-blue-400' : 'bg-slate-600'}`} style={{
            height: isPlaying ? `${Math.random() * 100}%` : '20%',
            animationDelay: `${i * 50}ms`
          }} data-id="element-2201" />)}
          </div>

          {/* Progress Bar */}
          <div className="mb-4" data-id="element-2202">
            <div className="h-1.5 bg-slate-700 rounded-full overflow-hidden" data-id="element-2203">
              <div className="h-full bg-gradient-to-r from-blue-500 to-blue-400 rounded-full transition-all duration-100" style={{
              width: `${progress}%`
            }} data-id="element-2204" />
            </div>
            <div className="flex justify-between mt-2 text-xs text-slate-400" data-id="element-2205">
              <span data-id="element-2206">{formatTime(currentTime)}</span>
              <span data-id="element-2207">{formatTime(duration)}</span>
            </div>
          </div>
        </div>

        {/* Controls */}
        <div className="bg-slate-800/50 p-6 flex items-center justify-center gap-6" data-id="element-2208">
          <button onClick={handleStop} className="w-14 h-14 rounded-full bg-red-500/20 flex items-center justify-center text-red-400 hover:bg-red-500/30 hover:text-red-300 transition-colors" title="Stop" data-id="element-2209">
            <StopCircleIcon size={28} data-id="element-2210" />
          </button>
          <button onClick={togglePlayPause} className="w-16 h-16 rounded-full bg-blue-500 flex items-center justify-center text-white hover:bg-blue-400 transition-colors shadow-lg shadow-blue-500/40" title={isPlaying ? 'Pause' : 'Play'} data-id="element-2211">
            {isPlaying ? <PauseIcon size={32} data-id="element-2212" /> : <PlayIcon size={32} fill="white" data-id="element-2213" />}
          </button>
          <button onClick={handleClose} className="w-14 h-14 rounded-full bg-slate-700 flex items-center justify-center text-slate-300 hover:bg-slate-600 transition-colors" title="End Call" data-id="element-2214">
            <PhoneIcon size={24} className="rotate-[135deg]" data-id="element-2215" />
          </button>
        </div>

        {/* Hidden Audio Element */}
        <audio ref={audioRef} src="https://call-em-quick.lovable.app/Call.m4a" preload="metadata" data-id="element-2216" />
      </div>
    </div>;
}
// Outbound Call Popup Component
function OutboundCallPopup({
  isOpen,
  onClose
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  if (!isOpen) return null;
  return <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center" onClick={onClose} data-id="element-2217">
        {/* Popup */}
        <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-sm w-full mx-4 transform transition-all" onClick={e => e.stopPropagation()} data-id="element-2218">
          <div className="flex items-center justify-between mb-4" data-id="element-2219">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center" data-id="element-2220">
              <PhoneCallIcon size={24} className="text-green-600" data-id="element-2221" />
            </div>
            <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors" data-id="element-2222">
              <XIcon size={18} data-id="element-2223" />
            </button>
          </div>

          <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-2224">
            Outbound Call Initiated
          </h3>
          <p className="text-sm text-gray-600 mb-5" data-id="element-2225">
            Outbound call initiated to this customer.
          </p>

          <button onClick={onClose} className="w-full py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors" data-id="element-2226">
            OK
          </button>
        </div>
      </div>
    </>;
}
export function LeadsManagement() {
  const navigate = useNavigate();
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [selectedChatLead, setSelectedChatLead] = useState<WhatsAppLead | null>(null);
  const [isDetailOverlayOpen, setIsDetailOverlayOpen] = useState(false);
  const [selectedDetailLead, setSelectedDetailLead] = useState<any>(null);
  const [isCallPopupOpen, setIsCallPopupOpen] = useState(false);
  const [isVoiceAgentOpen, setIsVoiceAgentOpen] = useState(false);
  const [voiceAgentLeadName, setVoiceAgentLeadName] = useState('');
  const handleOpenChat = (lead: Lead) => {
    const whatsappLead: WhatsAppLead = {
      id: lead.id,
      name: lead.name,
      phone: lead.phone,
      source: 'whatsapp',
      unreadCount: 0,
      aiScore: 75,
      status: 'active',
      tags: lead.tags || [],
      carModel: lead.carModel,
      dealer: lead.dealer
    };
    setSelectedChatLead(whatsappLead);
    setIsChatOpen(true);
  };
  const handleOpenDetailOverlay = (lead: Lead) => {
    setSelectedDetailLead({
      id: lead.id,
      name: lead.name,
      initials: lead.initials,
      email: lead.email,
      phone: lead.phone,
      callDuration: lead.recordingDuration || '12:21 Sec',
      leadType: lead.status,
      status: lead.type,
      carModel: lead.carModel
    });
    setIsDetailOverlayOpen(true);
  };
  const handleInitiateCall = (lead: Lead) => {
    // For the first lead (id: 1), open voice agent overlay
    if (lead.id === 1) {
      setVoiceAgentLeadName(lead.name);
      setIsVoiceAgentOpen(true);
    } else {
      setIsCallPopupOpen(true);
    }
  };
  return <div className="flex w-full min-h-screen bg-[#f5f7fa]" data-id="element-2227">
      <Sidebar data-id="element-2228" />
      <main className="flex-1 flex flex-col h-screen overflow-hidden" data-id="element-2229">
        <Header data-id="element-2230" />
        <div className="flex-1 overflow-y-auto p-6" data-id="element-2231">
          <h1 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-2232">Sales Leads</h1>

          {/* Table Container */}
          <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-id="element-2233">
            {/* Table Header */}
            <div className="grid grid-cols-[48px_1fr_1fr_1fr_1fr] border-b border-gray-200" data-id="element-2234">
              <div className="p-4 flex items-center justify-center" data-id="element-2235">
                <input type="checkbox" className="w-4 h-4 rounded border-gray-300" data-id="element-2236" />
              </div>
              <div className="p-4 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-2237">
                Lead Details
              </div>
              <div className="p-4 text-xs font-semibold text-gray-500 uppercase tracking-wider border-l border-gray-200" data-id="element-2238">
                Contact Information
              </div>
              <div className="p-4 text-xs font-semibold text-gray-500 uppercase tracking-wider border-l border-gray-200" data-id="element-2239">
                Dealer & Car Model
              </div>
              <div className="p-4 text-xs font-semibold text-gray-500 uppercase tracking-wider border-l border-gray-200" data-id="element-2240">
                Actions
              </div>
            </div>

            {/* Table Body */}
            {SALES_LEADS.map(lead => <div key={lead.id} className="grid grid-cols-[48px_1fr_1fr_1fr_1fr] border-b border-gray-100 last:border-b-0" data-id="element-2241">
                {/* Checkbox Column */}
                <div className="p-4 flex items-start justify-center pt-8 border-r border-gray-100" data-id="element-2242">
                  <input type="checkbox" className="w-4 h-4 rounded border-gray-300" data-id="element-2243" />
                </div>

                {/* Lead Details Column */}
                <div className="p-4 border-r border-gray-100" data-id="element-2244">
                  {lead.hasAI && <div className="flex items-center gap-1 text-blue-600 text-xs font-medium mb-3" data-id="element-2245">
                      <SparklesIcon size={14} data-id="element-2246" />
                      <span data-id="element-2247">AI</span>
                    </div>}

                  <div className="flex items-center gap-3 mb-3" data-id="element-2248">
                    <div className="w-11 h-11 rounded-lg bg-blue-100 flex items-center justify-center text-blue-600 font-bold text-base" data-id="element-2249">
                      {lead.initials}
                    </div>
                    <div data-id="element-2250">
                      <p className="font-semibold text-gray-900 text-sm" data-id="element-2251">
                        {lead.name}
                      </p>
                      <p className="text-xs text-gray-500" data-id="element-2252">{lead.type}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 flex-wrap" data-id="element-2253">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium border ${lead.status === 'cold' ? 'bg-blue-50 text-blue-600 border-blue-200' : lead.status === 'warm' ? 'bg-orange-50 text-orange-500 border-orange-200' : lead.status === 'hot' ? 'bg-red-50 text-red-500 border-red-200' : 'bg-gray-50 text-gray-600 border-gray-200'}`} data-id="element-2254">
                      {lead.status.charAt(0).toUpperCase() + lead.status.slice(1)}
                    </span>
                    {lead.tags?.map((tag, idx) => <span key={idx} className={`px-3 py-1 rounded-full text-xs font-medium border ${tag === 'Completed' ? 'bg-green-50 text-green-600 border-green-200' : tag === 'Partial' ? 'bg-orange-50 text-orange-500 border-orange-200' : 'bg-gray-50 text-gray-600 border-gray-200'}`} data-id="element-2255">
                        {tag}
                      </span>)}
                  </div>
                </div>

                {/* Contact Information Column */}
                <div className="p-4 border-r border-gray-100" data-id="element-2256">
                  <div className="space-y-2" data-id="element-2257">
                    <div className="flex items-center gap-2 text-sm text-gray-600" data-id="element-2258">
                      <MailIcon size={16} className="text-gray-400" data-id="element-2259" />
                      <span data-id="element-2260">{lead.email}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600" data-id="element-2261">
                      <PhoneIcon size={16} className="text-gray-400" data-id="element-2262" />
                      <span data-id="element-2263">{lead.phone}</span>
                    </div>

                    {lead.callStatus === 'pending' && <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-orange-50 text-orange-600 border border-orange-200 mt-1" data-id="element-2264">
                        <span className="w-1.5 h-1.5 bg-orange-500 rounded-full" data-id="element-2265"></span>
                        Pending
                      </span>}
                    {lead.callStatus === 'callback-requested' && <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200 mt-1" data-id="element-2266">
                        <span className="w-1.5 h-1.5 bg-blue-500 rounded-full" data-id="element-2267"></span>
                        Call Back Requested
                      </span>}
                  </div>
                </div>

                {/* Dealer & Car Model Column */}
                <div className="p-4 border-r border-gray-100" data-id="element-2268">
                  <div className="flex items-center gap-2 text-sm text-gray-600 mb-2" data-id="element-2269">
                    <BuildingIcon size={16} className="text-gray-400" data-id="element-2270" />
                    <span data-id="element-2271">
                      {lead.dealer} -{' '}
                      <span className="font-semibold text-gray-900" data-id="element-2272">
                        {lead.dealerCode}
                      </span>
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600 mb-3" data-id="element-2273">
                    <CarIcon size={16} className="text-gray-400" data-id="element-2274" />
                    <span data-id="element-2275">{lead.carModel}</span>
                  </div>

                  {lead.hasRecording && lead.recordingDuration ? <div className="inline-flex items-center gap-2 bg-blue-50 rounded-full px-3 py-1.5 border border-blue-100" data-id="element-2276">
                      <button className="text-gray-700 hover:text-blue-600" data-id="element-2277">
                        <PlayIcon size={12} fill="currentColor" data-id="element-2278" />
                      </button>
                      <span className="text-xs text-gray-600" data-id="element-2279">
                        0:00 / {lead.recordingDuration}
                      </span>
                      <div className="w-12 h-1 bg-blue-200 rounded-full relative" data-id="element-2280">
                        <div className="absolute left-0 top-0 h-1 w-0 bg-blue-500 rounded-full" data-id="element-2281"></div>
                      </div>
                      <button className="text-gray-700 hover:text-blue-600" data-id="element-2282">
                        <Volume2Icon size={12} data-id="element-2283" />
                      </button>
                    </div> : <div className="inline-flex px-3 py-1.5 bg-gray-100 rounded-full border border-gray-200" data-id="element-2284">
                      <span className="text-xs text-gray-500" data-id="element-2285">
                        Recording Not Available
                      </span>
                    </div>}
                </div>

                {/* Actions Column */}
                <div className="p-4" data-id="element-2286">
                  {lead.multiVisit && <div className="flex justify-end mb-3" data-id="element-2287">
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-semibold bg-green-50 text-green-600 border border-green-200 uppercase" data-id="element-2288">
                        <RefreshCwIcon size={10} data-id="element-2289" />
                        Multi-Visits
                      </span>
                    </div>}

                  <div className="flex items-center justify-end gap-2" data-id="element-2290">
                    <button className="w-9 h-9 rounded-full border-2 border-blue-200 flex items-center justify-center text-blue-500 hover:bg-blue-50 hover:border-blue-300 transition-colors" title="Schedule" data-id="element-2291">
                      <ClockIcon size={16} data-id="element-2292" />
                    </button>
                    <button onClick={() => handleInitiateCall(lead)} className="w-9 h-9 rounded-full border-2 border-blue-200 flex items-center justify-center text-blue-500 hover:bg-blue-50 hover:border-blue-300 transition-colors" title="Call" data-id="element-2293">
                      <PhoneIcon size={16} data-id="element-2294" />
                    </button>
                    <button onClick={() => handleOpenChat(lead)} className="w-9 h-9 rounded-full border-2 border-blue-200 flex items-center justify-center text-blue-500 hover:bg-blue-50 hover:border-blue-300 transition-colors" title="WhatsApp" data-id="element-2295">
                      <MessageCircleIcon size={16} data-id="element-2296" />
                    </button>
                    <button onClick={() => handleOpenDetailOverlay(lead)} className="w-9 h-9 rounded-full border-2 border-blue-200 flex items-center justify-center text-blue-500 hover:bg-blue-50 hover:border-blue-300 transition-colors" title="Edit" data-id="element-2297">
                      <EditIcon size={16} data-id="element-2298" />
                    </button>
                  </div>

                  {lead.aiResponse && <p className="text-xs text-gray-500 mt-3 text-right" data-id="element-2299">
                      {lead.aiResponse}
                    </p>}
                </div>
              </div>)}
          </div>
        </div>

        <WhatsAppChatModal isOpen={isChatOpen} onClose={() => setIsChatOpen(false)} lead={selectedChatLead} data-id="element-2300" />
        <LeadDetailOverlay isOpen={isDetailOverlayOpen} onClose={() => setIsDetailOverlayOpen(false)} lead={selectedDetailLead} data-id="element-2301" />
        <OutboundCallPopup isOpen={isCallPopupOpen} onClose={() => setIsCallPopupOpen(false)} data-id="element-2302" />
        <VoiceAgentOverlay isOpen={isVoiceAgentOpen} onClose={() => setIsVoiceAgentOpen(false)} leadName={voiceAgentLeadName} data-id="element-2303" />
      </main>
    </div>;
}